// Single lazy identity for the heavy (three.js-transitive) views.
//
// WHY: the Lazy3D boundary was being defeated by parallel import paths — the
// router lazy-loaded EditorView/FilesView/ShareView while the tab shell
// (LayoutContent → FilesView, EditorTabPane → EditorView) statically imported
// the same modules, pulling the `three` chunk back onto the landing-page
// critical path (finding A2, docs/reviews/2026-07-priorities-fanout.md).
// Every consumer outside the views' own modules imports from HERE, so the lazy
// boundary is singular and structural. Verified via `npm run build`: dist/
// index.html must not modulepreload the `three` chunk.
//
// Composed as `lazy(importWithRetry(...))` rather than `lazyWithRetry` because
// the latter's `ComponentType<unknown>` constraint erases prop types — the tab
// shell needs EditorView's real `{ conversationId, isActive }` props. Retry +
// ChunkLoadError tagging behavior is identical (same importWithRetry).
import { lazy } from 'react';
import { importWithRetry } from '@/lib/lazyWithRetry';

export const EditorView = lazy(() =>
  importWithRetry(() => import('./EditorView.tsx')),
);

export const FilesView = lazy(() =>
  importWithRetry(() =>
    import('./FilesView.tsx').then((m) => ({ default: m.FilesView })),
  ),
);

export const ShareView = lazy(() =>
  importWithRetry(() => import('./ShareView.tsx')),
);

// Marketplace isn't three.js-transitive, but it drags the whole product
// catalog (72 kB of JSON) plus its card/sidebar/header modules. Statically
// imported by the tab shell it landed in the entry chunk, so every landing-page
// visitor downloaded the catalog before seeing anything. Same boundary, same
// reason as the views above.
export const MarketplaceView = lazy(() =>
  importWithRetry(() =>
    import('./MarketplaceView.tsx').then((m) => ({
      default: m.MarketplaceView,
    })),
  ),
);
